import {isEnergyDemand} from "../energy-scale";

/** Einstufung des Energieverbrauchs (ohne vorliegenden Energieausweis) je Effizienzklasse, die Skala
 * A+ bis H gleichmäßig gedrittelt (#18998). */
const ENERGY_EFFICIENCY_CONSUMPTION_LEVEL: Readonly<Record<string, string>> = {
    "A+": "niedriger",
    A: "niedriger",
    B: "niedriger",
    C: "durchschnittlicher",
    D: "durchschnittlicher",
    E: "durchschnittlicher",
    F: "erhöhter",
    G: "erhöhter",
    H: "erhöhter",
};

const HEATING_REPLACEMENT_ADDENDUM =
    "Die Heizungsanlage hat kurz- bis mittelfristig das Ende ihrer Nutzungsdauer erreicht. Für die " +
    "Erneuerung der Heizungsanlage fallen somit künftig Kosten an, die im Rahmen der Bewertung " +
    "sachverständig zu berücksichtigen sind. Im Rahmen einer unter Sicherheitsaspekten stehenden " +
    "Bewertung bringt der Sachverständige Ersatzbeschaffungskosten bei den Besonderen objektspezifischen " +
    "Merkmalen in Abzug. Hierbei handelt es sich nicht um eine exakte Berechnung der tatsächlich anfallenden " +
    "Kosten, sondern um eine grob überschlägige Schätzung des Einflusses auf den Immobilienwert.";

export interface EnergyAppreciationInput {
    /** Ob belastbare Energieausweis-Kennwerte vorliegen (siehe {@link hasEnergyCertificateData}). */
    hasCertificate: boolean;
    /** Effizienzklasse (A+ bis H) aus dem Energiewert-Output; ohne Energieausweis nur die AVM-Schätzung. */
    energyEfficiency: string | null | undefined;
    /** energy_type-Output: 1 = Bedarfsausweis, 0/sonst = Verbrauchsausweis. */
    energyType: number | null | undefined;
    /** true, wenn die Heizungsanlage zum Wertermittlungsstichtag 30 Jahre oder älter ist. */
    heatingOlderThan30Years: boolean;
}

function normalizeEfficiencyClass(energyEfficiency: string | null | undefined): string {
    return typeof energyEfficiency === "string" ? energyEfficiency.trim().toUpperCase() : "";
}

/**
 * Default-Würdigungstext zur energetischen Einstufung (#18998), je nachdem ob ein Energieausweis
 * vorliegt, plus optionalem Zusatz zur Heizungsanlage.
 */
export function buildEnergyAppreciationText(input: EnergyAppreciationInput): string {
    const {hasCertificate, energyEfficiency, energyType, heatingOlderThan30Years} = input;
    const mainText = hasCertificate
        ? buildCertificatePresentText(energyEfficiency, energyType)
        : buildNoCertificateText(energyEfficiency);
    return heatingOlderThan30Years ? `${mainText}\n${HEATING_REPLACEMENT_ADDENDUM}` : mainText;
}

function buildCertificatePresentText(
    energyEfficiency: string | null | undefined,
    energyType: number | null | undefined,
): string {
    const certificateType = isEnergyDemand(energyType) ? "Bedarfsausweis" : "Verbrauchsausweis";
    const efficiencyClass = normalizeEfficiencyClass(energyEfficiency);
    return `Nach dem vorliegenden ${certificateType} verfügt das Objekt über eine Effizienzklasse ${efficiencyClass} auf einer Skala von A+ bis H. Die energetischen Eigenschaften des Objektes sind in den angesetzten Parametern enthalten.`;
}

function buildNoCertificateText(energyEfficiency: string | null | undefined): string {
    const consumptionLevel =
        ENERGY_EFFICIENCY_CONSUMPTION_LEVEL[normalizeEfficiencyClass(energyEfficiency)] ?? "durchschnittlicher";
    return `Ein aktueller Energieausweis lag zum Wertermittlungsstichtag nicht vor. Unter Berücksichtigung der Gebäudeeigenschaften sowie der Erkenntnisse bei der Besichtigung wird ein ${consumptionLevel} Energieverbrauch plausibel zugrunde gelegt. Die energetischen Eigenschaften des Objektes sind in den angesetzten Parametern enthalten.`;
}

export function isHeatingOlderThan30Years(heatingModernizationYear: unknown, valuationDate: unknown): boolean {
    const valuationYear =
        valuationDate != null && typeof valuationDate === "string" ? new Date(valuationDate).getFullYear() : null;
    if (valuationYear == null || !Number.isFinite(valuationYear) || typeof heatingModernizationYear !== "number")
        return false;
    return valuationYear - heatingModernizationYear >= 30;
}
